import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateUiState } from 'redux/slices/uiSlice';
import { SidebarNav } from './components';
import NavRail from './components/NavRail';
import { activeGroupKey, useNavGroups } from './navGroups';
import {
  ONE_LEVEL_SIDEBAR_PX,
  RAIL_PX,
  TWO_LEVEL_PANEL_PX,
} from '../../sidebarMetrics';
import { TOPBAR_PX } from '../../pageMetrics';

/**
 * The navigation groups' `navCollapsedGroups` keys -- the trimmed `NavItem`
 * titles, which is what the panel's collapse toggle stores. The standalone
 * items (Mailbox, Action Plan, Reports) have nothing to collapse.
 */
const COLLAPSE_KEYS = {
  deals: 'Deals',
  tools: 'Tools',
  askAi: 'Ask AI',
  myOrg: 'My Organization',
  setup: 'Setup',
};

const Sidebar = ({
  open,
  variant,
  onClose,
  expanded,
  twoLevel = false,
  underTopbar = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navCollapsedGroups = useSelector(
    (state) => state.uiStates.navCollapsedGroups,
  );

  const groups = useNavGroups();
  const routeGroup = activeGroupKey(groups, pathname);

  // The rail's selection follows the route; a rail pick overrides it until
  // the next navigation.
  const [selectedGroup, setSelectedGroup] = useState(routeGroup);
  const [scrollRequest, setScrollRequest] = useState(
    routeGroup ? { key: routeGroup, mode: 'start', smooth: false } : null,
  );

  useEffect(() => {
    setSelectedGroup(routeGroup);
    if (routeGroup) {
      setScrollRequest({ key: routeGroup, mode: 'ifNeeded', smooth: true });
    }
  }, [pathname, routeGroup]);

  // Only called while the panel is open: a rail click with the panel
  // collapsed just navigates (see `NavItem`'s rail variant) and leaves it shut.
  const handleSelectGroup = useCallback(
    (key) => {
      setSelectedGroup(key);
      // A group the user collapsed in the panel would scroll into view as a
      // bare heading, so picking it on the rail opens it again.
      const collapseKey = COLLAPSE_KEYS[key];
      if (collapseKey && navCollapsedGroups?.[collapseKey]) {
        dispatch(
          updateUiState({
            key: 'navCollapsedGroups',
            value: { ...navCollapsedGroups, [collapseKey]: false },
          }),
        );
      }
      // A fresh object each time, so re-picking the same group scrolls again.
      setScrollRequest({ key, mode: 'start', smooth: true });
    },
    [dispatch, navCollapsedGroups],
  );

  const paperWidth = twoLevel
    ? expanded
      ? RAIL_PX + TWO_LEVEL_PANEL_PX
      : RAIL_PX
    : expanded
    ? ONE_LEVEL_SIDEBAR_PX
    : RAIL_PX;

  // What the sidebar is showing, for the product tours: `panel` lists every
  // page, `rail` is the two-level rail on its own (hover a tile for its
  // pages), `icons` the collapsed one-level sidebar (click a group for them).
  const navLayout = expanded ? 'panel' : twoLevel ? 'rail' : 'icons';

  return (
    <Drawer
      anchor="left"
      onClose={() => onClose()}
      open={open}
      variant={variant}
      sx={{
        '& .MuiPaper-root': {
          width: '100%',
          maxWidth: paperWidth,
          height: '100vh',
          // The drawer paper scrolls on its own (MUI gives it `overflowY:
          // auto`), so it gets the same vertical-only rule as the nav.
          overflowX: 'hidden',
          backgroundColor: theme.palette.custom.sidebar,
          // borderRight: `1px solid ${theme.palette.background.level1}`,
          // Two-level: the sidebar sits *under* the topbar, which starts at
          // the rail's right edge (see `Dashboard`). The rail and the topbar
          // meet as one L-shaped frame, and the nav panel runs up behind the
          // topbar rather than beside it.
          ...(twoLevel ? { zIndex: theme.zIndex.appBar - 1 } : {}),
          borderRight: 'none',
        },
      }}
    >
      {twoLevel ? (
        <Box
          data-testid="sidebar"
          data-tour="sidebar-nav"
          data-nav-layout={navLayout}
          sx={{ height: '100%', display: 'flex' }}
        >
          <NavRail
            selectedGroup={selectedGroup}
            onSelectGroup={handleSelectGroup}
            underTopbar={underTopbar}
          />
          {expanded ? (
            <Box
              data-testid="sidebar-panel"
              sx={{
                width: TWO_LEVEL_PANEL_PX,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                // The strip behind the topbar holds nothing, so the nav starts
                // below it.
                paddingTop: underTopbar ? `${TOPBAR_PX}px` : 0,
              }}
            >
              <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <SidebarNav
                  onClose={onClose}
                  expanded={expanded}
                  twoLevel
                  scrollRequest={scrollRequest}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : (
        <Box
          data-testid="sidebar"
          data-tour="sidebar-nav"
          data-nav-layout={navLayout}
          sx={{
            height: '100%',
          }}
        >
          <SidebarNav onClose={onClose} expanded={expanded} />
        </Box>
      )}
    </Drawer>
  );
};

Sidebar.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
  twoLevel: PropTypes.bool,
  underTopbar: PropTypes.bool,
};

export default Sidebar;
